/**
 * @FileDescription: 接口类模板
 * @Author: fanqixin
 * @Date: 2021-6-30 13:57:53
 * @LastEditors: chenw
 * @LastEditTime: 2025-4-28 17:56:53
 */
import axios from '@/api/utils/axios';
import store from '@/store';
import utils from '@/utils/index.js';
import Message from '@/utils/message.js';

let needFetchEnumClass = [];
let fetchEnumTimeout = null;
let pendingPromise = null;
let pendingResolve = null;

/**
 * @description: 查询枚举类
 * @param {Object} enumClass 枚举类类名
 * @param {Object} isLoadCache 是否缓存中获取
 */
function getByEnumClass(enumClass, isLoadCache) {
	const promise = new Promise(resolve => {
		let enumClassList = [];
		let enumObj = {};
		if (isLoadCache == null || isLoadCache) {
			enumObj = store.getters["enumStore/getEnum"](enumClass);
			for (let key in enumObj) {
				if (enumObj[key] == null) {
					enumClassList.push(key);
				}
			}
			if (enumClassList.length === 0) {
				return resolve({
					code: '00000',
					model: enumObj
				});
			}
		} else {
			enumClassList = enumClass;
		}
		axios.post(this.baseUrl() + "/getByEnumClass", {
			ids: enumClassList,
		}).then(res => {
			if (res && res.code === "00000") {
				store.commit('enumStore/setEnum', res.model);
				return resolve({
					code: '00000',
					model: Object.assign(enumObj, res.model)
				});
			} else {
				return resolve(null);
			}
		});
	});
	return promise;
}

const BaseApi = {
	/**
	 * @description: 根据id获取详情
	 * @param {String} id 数据id
	 */
	getById: function(id) {
		return axios.get(this.baseUrl() + "/" + id);
	},
	/**
	 * @description: 获取唯一编号
	 * @param {String} id 数据id
	 */
	getUniqueCode: function() {
		return axios.get(this.baseUrl() + "/getUniqueCode");
	},
	/**
	 * @description: 新增数据
	 * @param {Object} model 数据模型
	 */
	addModel: function(model) {
		return axios.post(this.baseUrl(), model);
	},
	/**
	 * @description: 根据id编辑数据
	 * @param {String} id 数据id
	 * @param {Object} model 数据模型
	 */
	editModel: function(id, model) {
		return axios.put(this.baseUrl() + "/" + id, model);
	},
	/**
	 * @description: 根据id集合批量删除
	 * @param {Array} ids id集合
	 */
	deleteModel: function(ids) {
		return axios.delete(this.baseUrl() + '/deleteMany', {
			data: {
				ids: Array.isArray(ids) ? ids : [ids]
			}
		});
	},
	/**
	 * @description: 根据limit查询列表数据
	 * @param {Object} limit 搜索分页等限制参数
	 * @param {Object} params params参数
	 */
	query: function(limit, params) {
		return axios.post(this.baseUrl() + "/list", limit, {
			params: params
		});
	},
	/**
	 * @description: 根据limit查询列表数据
	 * @param {Object} limit 搜索分页等限制参数
	 * @param {Object} params params参数
	 */
	queryLimit: function(limit, params) {
		return axios.post(this.baseUrl() + "/queryLimit", limit, {
			params: params
		});
	},
	/**
	 * @description: 根据limit查询有效的列表数据
	 * @param {Object} limit 条件限制参数
	 * @param {Object} params params参数
	 */
	listByEnabled: function(limit, params) {
		return axios.post(this.baseUrl() + "/listByEnabled", limit, {
			params: params
		});
	},
	/**
	 * @description: 根据limit查询统计总额
	 * @param {Object} limit 搜索分页等限制参数
	 * @param {Object} params params参数
	 */
	querySum: function(limit, params) {
		return axios.post(this.baseUrl() + "/list/sum", limit, {
			params: params
		});
	},
	
	/**
	 * @description: 查询枚举类
	 * @param {Object} enumClass 枚举类类名
	 * @param {Object} isLoadCache 是否缓存中获取
	 * @return {Promise} Promise对象，返回枚举类数据
	 */
	getByEnumClass: function(enumClass, isLoadCache) {
		enumClass = Array.isArray(enumClass) ? enumClass : [enumClass];
		needFetchEnumClass = [...new Set([...enumClass, ...needFetchEnumClass])];
		
		// 如果当前没有等待执行的Promise，创建一个新的
		if (!pendingPromise) {
			pendingPromise = new Promise(resolve => {
				pendingResolve = resolve;
			});
		}

		if (fetchEnumTimeout) {
			clearTimeout(fetchEnumTimeout);
		}
		
		fetchEnumTimeout = setTimeout(() => {
			const currentEnumClass = [...needFetchEnumClass]; // 复制当前收集的所有枚举类
			const currentResolve = pendingResolve;
			
			// 重置状态，为下一次请求做准备
			needFetchEnumClass = [];
			pendingPromise = null;
			pendingResolve = null;
			fetchEnumTimeout = null;
			
			// 执行实际的请求
			getByEnumClass.call(this, currentEnumClass, isLoadCache)
				.then(result => {
					currentResolve(result);
				});
		}, 500);
		
		return pendingPromise;
	},
	/**
	 * @description: 根据id判断数据能否发起流程
	 * @param {String} entityId  数据实体id
	 */
	sendCapable: function(entityId) {
		return axios.get(this.baseUrl() + "/workflow/capable/" + entityId);
	},
	/**
	 * @description: 发送流程
	 * @param {String} entityId  数据实体id
	 * @param {Object} workflow  流程信息
	 */
	sendWorkflow: function(entityId, workflow) {
		return axios.put(this.baseUrl() + "/workflow/send/" + entityId, workflow);
	},
	/**
	 * @description: 撤回流程
	 * @param {String} entityId  数据实体id
	 */
	callback: function(entityId) {
		return axios.get(this.baseUrl() + "/workflow/callback/" + entityId);
	},
	/**
	 * @description: 反审核
	 * @param {String} entityId  数据实体id
	 */
	unAudit: function(entityId) {
		return axios.put(this.baseUrl() + "/workflow/unAudit/" + entityId);
	},
	/**
	 * @description: 导入数据
	 * @param {String} docFileId  文件id
	 */
	import: function(docFileId) {
		return axios.post(this.baseUrl() + '/importData', {docFileId: docFileId});
	},
	/**
	 * @description: 导入数据
	 * @param {String} docFileId  文件id
	 */
	downTemplate: function(templateName) {
		return axios.post(this.baseUrl() + '/downTemplate', {templateName: templateName}, {
			params: {
				loading: true
			},
			responseType: "blob"
		}).then(res => {
			if (res == null) {
				return;
			}
			if (res.headers.exception != null) {
				Message.error({
					message: decodeURIComponent(res.headers.exception),
					duration: 5 * 1000,
					showClose: true
				});
				return;
			}
			let filename = null;
			if (res.headers != null && res.headers.filename != null) {
				filename = decodeURIComponent(res.headers.filename);
			}
			if (filename == null) {
				filename = "文件下载" + utils.formatDate(new Date(), "yyyyMMddHHmmssSSS") + ".xlsx";
			}
			const link = document.createElement('a');
			let blob = new Blob([res.data], {
				type: 'application/vnd.ms-excel'
			});
			link.style.display = 'none';
			link.href = URL.createObjectURL(blob);
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		});
	},
	/**
	 * @description: 根据limit导出
	 * @param {Object} limit 搜索分页等限制参数
	 * @param {Object} params params参数
	 */
	export: function(limit, params, url = '/exportExcel') {
		return axios.post(this.baseUrl() + url, limit, {
			responseType: "blob"
		}).then(res => {
			if (res == null) {
				return;
			}
			if (res.headers.exception != null) {
				Message.error({
					message: decodeURIComponent(res.headers.exception),
					duration: 5 * 1000,
					showClose: true
				});
				return;
			}
			let filename = null;
			if (res.headers != null && res.headers.filename != null) {
				filename = decodeURIComponent(res.headers.filename);
			}
			if (filename == null) {
				filename = "文件下载" + utils.formatDate(new Date(), "yyyyMMddHHmmssSSS") + ".xlsx";
			}
			const link = document.createElement('a');
			let blob = new Blob([res.data], {
				type: 'application/vnd.ms-excel'
			});
			link.style.display = 'none';
			link.href = URL.createObjectURL(blob);
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		});
	},
};

export default BaseApi;
